// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — bootstraps Pixpress: wires the drop zone, format/quality/resize
 * controls, results grid, before/after comparison, output delivery and modals
 * to the worker-backed Compressor. Owns no heavy logic.
 */

import { Compressor, type CompressSettings } from './compress';
import {
  DEFAULT_FORMAT,
  DEFAULT_MAX_DIM,
  DEFAULT_QUALITY,
  FORMATS,
  formatBytes,
  isOutputFormat,
  sizeDeltaPct,
  type OutputFormat,
} from './image';
import { emit, mountEventDrawer } from './eventlog';
import { initGlossary, term } from './glossary';
import { closeModal, el, escapeHtml, isModalOpen, openModal, toast } from './ui';
import { closeCompare, isCompareOpen, openCompare } from './compare';
import type { CompressItem } from './types';

const LS_FORMAT = 'pixpress:format';
const LS_QUALITY = 'pixpress:quality';
const LS_MAXDIM = 'pixpress:maxdim';

const RESIZE_PRESETS = [
  { value: 0, label: 'No limit' },
  { value: 2560, label: '2560 px' },
  { value: 1920, label: '1920 px' },
  { value: 1280, label: '1280 px' },
  { value: 1024, label: '1024 px' },
  { value: 640, label: '640 px' },
];

// ─────────────────────────── settings (persisted) ───────────────────────────

function loadSettings(): CompressSettings {
  let format: OutputFormat = DEFAULT_FORMAT;
  const storedF = localStorage.getItem(LS_FORMAT);
  if (storedF && isOutputFormat(storedF)) format = storedF;

  let quality = DEFAULT_QUALITY;
  const q = Number(localStorage.getItem(LS_QUALITY));
  if (Number.isFinite(q) && q >= 0.3 && q <= 1) quality = q;

  let maxDim = DEFAULT_MAX_DIM;
  const m = Number(localStorage.getItem(LS_MAXDIM));
  if (Number.isFinite(m) && RESIZE_PRESETS.some((p) => p.value === m)) maxDim = m;

  return { format, quality, maxDim };
}

const settings = loadSettings();

// ─────────────────────────────── DOM scaffold ───────────────────────────────

const app = document.getElementById('app')!;
const drawer = document.getElementById('event-drawer')!;
const sbDot = document.getElementById('sb-dot')!;
const sbStatus = document.getElementById('sb-status')!;
const sbThroughput = document.getElementById('sb-throughput')!;

app.innerHTML = `
  <section class="tool">
    <div class="controls">
      <div class="control-group">
        <span class="control-label">Format</span>
        <div class="seg" role="radiogroup" aria-label="Output format">
          ${(Object.keys(FORMATS) as OutputFormat[])
            .map(
              (f) =>
                `<button type="button" class="seg-btn" role="radio" data-format="${f}"
                   aria-checked="${f === settings.format}" title="${FORMATS[f].hint}">${FORMATS[f].label}</button>`,
            )
            .join('')}
        </div>
      </div>
      <div class="control-group quality" id="quality-group">
        <label class="control-label" for="quality">Quality</label>
        <input type="range" id="quality" min="0.3" max="1" step="0.01" value="${settings.quality}" />
        <span class="quality-val" id="quality-val">${Math.round(settings.quality * 100)}</span>
      </div>
      <div class="control-group">
        <label class="control-label" for="resize">Max&nbsp;size</label>
        <select id="resize" class="select">
          ${RESIZE_PRESETS.map(
            (p) => `<option value="${p.value}" ${p.value === settings.maxDim ? 'selected' : ''}>${p.label}</option>`,
          ).join('')}
        </select>
      </div>
    </div>

    <div class="dropzone" id="dropzone" tabindex="0" role="button"
         aria-label="Drop images here, or click to choose files">
      <svg class="dz-icon" viewBox="0 0 24 24" width="46" height="46" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
        <circle cx="8.5" cy="9" r="1.6" fill="currentColor"/>
        <path d="M4 17l5-5 3.5 3.5L16 11l4 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <div class="dz-title">Drop images to compress</div>
      <div class="dz-sub">or click to browse · paste with ⌘/Ctrl&nbsp;+&nbsp;V</div>
      <div class="dz-formats">JPEG · PNG · WebP · AVIF · GIF · BMP — whole batches welcome, everything stays on your device</div>
      <button type="button" class="sample-link" id="sample-btn">
        <svg class="ic" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" fill="currentColor"/>
          <path d="M19 14l.7 1.9L21.5 17l-1.8.7L19 19.5l-.7-1.8L16.5 17l1.8-.7L19 14z" fill="currentColor"/>
        </svg>
        Try a sample image
      </button>
      <input type="file" id="file-input" class="visually-hidden"
             accept="image/*" multiple />
    </div>

    <div class="overall hidden" id="overall" aria-live="polite">
      <div class="overall-bar"><div class="overall-fill" id="overall-fill"></div></div>
      <div class="overall-text" id="overall-text"></div>
    </div>

    <div class="summary hidden" id="summary" aria-live="polite"></div>

    <div class="results" id="results" aria-live="polite"></div>

    <div class="batch-actions hidden" id="batch-actions">
      <button type="button" class="btn btn-primary" id="download-all">Download all (ZIP)</button>
      <button type="button" class="btn btn-ghost" id="clear-all">Clear</button>
    </div>

    <p class="privacy-note">
      Runs entirely in your browser with ${term('MozJPEG, WebP, AVIF &amp; OxiPNG', 'mozjpeg')} compiled to
      ${term('WebAssembly', 'wasm')}. Your images never leave your device —
      <button type="button" class="link-btn" data-modal="tmpl-security">read the threat model</button>.
    </p>
  </section>
`;

// ───────────────────────────── element handles ──────────────────────────────

const dropzone = document.getElementById('dropzone') as HTMLElement;
const fileInput = document.getElementById('file-input') as HTMLInputElement;
const results = document.getElementById('results') as HTMLElement;
const overall = document.getElementById('overall') as HTMLElement;
const overallFill = document.getElementById('overall-fill') as HTMLElement;
const overallText = document.getElementById('overall-text') as HTMLElement;
const summary = document.getElementById('summary') as HTMLElement;
const batchActions = document.getElementById('batch-actions') as HTMLElement;
const qualityGroup = document.getElementById('quality-group') as HTMLElement;
const qualityInput = document.getElementById('quality') as HTMLInputElement;
const qualityVal = document.getElementById('quality-val') as HTMLElement;
const resizeSelect = document.getElementById('resize') as HTMLSelectElement;

// ─────────────────────────────── event log ──────────────────────────────────

mountEventDrawer(drawer);
initGlossary();
emit('info', 'Pixpress ready — drop an image to compress. Nothing is uploaded.');

// ──────────────────────────────── compressor ────────────────────────────────

const cards = new Map<number, HTMLElement>();

const compressor = new Compressor(
  {
    onItem: (item) => {
      renderCard(item);
      if (item.status === 'done') {
        updateSummary();
        refreshOpenCompare(item);
      }
    },
    onOverall: (done, total, bps) => renderOverall(done, total, bps),
    onLog: (level, msg, meta) => emit(level, msg, meta),
    onIdle: () => {
      setStatus('Ready', 'idle');
      overall.classList.add('hidden');
      sbThroughput.textContent = '';
      updateBatchActions();
      updateSummary();
    },
  },
  settings,
);

// ─────────────────────────────── rendering ──────────────────────────────────

function setStatus(text: string, state: 'idle' | 'busy' | 'error'): void {
  sbStatus.textContent = text;
  sbDot.dataset.state = state;
}

function renderOverall(done: number, total: number, bps: number): void {
  if (total === 0) {
    overall.classList.add('hidden');
    return;
  }
  overall.classList.remove('hidden');
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  overallFill.style.width = `${pct}%`;
  overallText.textContent = `${done} / ${total} done`;
  if (done < total) {
    setStatus(`Compressing ${done + 1} of ${total}…`, 'busy');
    sbThroughput.textContent = bps > 0 ? `${formatBytes(bps)}/s` : '';
  }
}

function updateSummary(): void {
  const done = compressor.successful();
  if (done.length < 1) {
    summary.classList.add('hidden');
    return;
  }
  let inSum = 0;
  let outSum = 0;
  for (const i of done) {
    inSum += i.inputSize;
    outSum += i.outputSize ?? 0;
  }
  const delta = sizeDeltaPct(inSum, outSum);
  const saved = delta <= 0;
  summary.classList.remove('hidden');
  summary.innerHTML = `
    <span class="summary-count">${done.length} image${done.length === 1 ? '' : 's'}</span>
    <span class="summary-sizes">${formatBytes(inSum)} → <strong>${formatBytes(outSum)}</strong></span>
    <span class="summary-delta ${saved ? 'down' : 'up'}">${delta > 0 ? '+' : ''}${delta}%</span>`;
}

function canShareFiles(): boolean {
  return typeof navigator.canShare === 'function' && typeof navigator.share === 'function';
}

function renderCard(item: CompressItem): void {
  let card = cards.get(item.id);
  if (!card) {
    card = el('article', 'card');
    card.dataset.id = String(item.id);
    cards.set(item.id, card);
    results.appendChild(card);
  }
  card.classList.toggle('error', item.status === 'error');

  const busy = item.status === 'queued' || item.status === 'decoding' || item.status === 'encoding';
  const thumbSrc = item.outUrl ?? item.srcUrl;
  const thumb =
    item.status === 'error'
      ? `<div class="card-badge err">!</div>`
      : thumbSrc
        ? `<img src="${thumbSrc}" alt="${escapeHtml(item.name)}" loading="lazy" draggable="false" />${
            busy ? '<div class="card-working"><div class="card-spinner"></div></div>' : ''
          }`
        : `<div class="card-spinner" aria-hidden="true"></div>`;

  let meta = '';
  if (item.status === 'done') {
    const delta = sizeDeltaPct(item.inputSize, item.outputSize ?? 0);
    const cls = delta <= 0 ? 'down' : 'up';
    const sign = delta > 0 ? '+' : '';
    meta = `
      <div class="card-meta">
        <span class="dim">${item.width ?? item.srcWidth}×${item.height ?? item.srcHeight}</span>
        <span>${formatBytes(item.inputSize)} → <strong>${formatBytes(item.outputSize ?? 0)}</strong></span>
        <span class="delta ${cls}">${sign}${delta}%</span>
      </div>`;
  } else if (item.status === 'error') {
    meta = `<div class="card-error">${escapeHtml(item.error ?? 'Compression failed.')}</div>`;
  } else {
    const label = item.status === 'encoding' ? 'Encoding…' : item.status === 'decoding' ? 'Decoding…' : 'Queued…';
    meta = `<div class="card-meta"><span class="working">${label}</span></div>`;
  }

  const actions =
    item.status === 'done'
      ? `<div class="card-actions">
           <button type="button" class="card-btn primary" data-act="compare">Compare</button>
           <button type="button" class="card-btn" data-act="download">Save</button>
           <button type="button" class="card-btn" data-act="copy" aria-label="Copy to clipboard">Copy</button>
           ${canShareFiles() ? `<button type="button" class="card-btn" data-act="share">Share</button>` : ''}
         </div>`
      : '';

  card.innerHTML = `
    <button type="button" class="card-thumb" data-act="${item.status === 'done' ? 'compare' : ''}"
      aria-label="${item.status === 'done' ? 'Compare original and compressed' : escapeHtml(item.name)}">${thumb}</button>
    <div class="card-body">
      <div class="card-name" title="${escapeHtml(item.outName ?? item.name)}">${escapeHtml(item.outName ?? item.name)}</div>
      ${meta}
      ${actions}
    </div>
    <button type="button" class="card-remove" data-act="remove" aria-label="Remove">&times;</button>`;

  card.querySelectorAll<HTMLElement>('[data-act]').forEach((btn) => {
    const act = btn.dataset.act;
    if (!act) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      handleCardAction(act, item);
    });
  });
}

function updateBatchActions(): void {
  batchActions.classList.toggle('hidden', compressor.successful().length < 2);
}

function refreshOpenCompare(item: CompressItem): void {
  // If the comparison overlay is open for this item, refresh it after a re-encode.
  if (isCompareOpen() && document.querySelector(`.cmp-overlay[data-id="${item.id}"]`)) {
    openCompare(item);
    const ov = document.querySelector('.cmp-overlay');
    ov?.setAttribute('data-id', String(item.id));
  }
}

// ─────────────────────────── output delivery ────────────────────────────────

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function handleCardAction(act: string, item: CompressItem): Promise<void> {
  if (act === 'remove') {
    compressor.remove(item.id);
    cards.get(item.id)?.remove();
    cards.delete(item.id);
    updateBatchActions();
    updateSummary();
    if (compressor.items().length === 0) {
      overall.classList.add('hidden');
      batchActions.classList.add('hidden');
    }
    emit('info', `Removed ${item.name}`);
    return;
  }
  if (act === 'compare') {
    const fresh = compressor.get(item.id) ?? item;
    openCompare(fresh);
    document.querySelector('.cmp-overlay')?.setAttribute('data-id', String(item.id));
    return;
  }
  if (!item.blob || !item.outName) return;
  try {
    if (act === 'download') {
      downloadBlob(item.blob, item.outName);
      emit('ok', `Saved ${item.outName}`);
    } else if (act === 'copy') {
      if (!('clipboard' in navigator) || typeof ClipboardItem === 'undefined') {
        throw new Error('Clipboard image copy is not supported in this browser.');
      }
      await navigator.clipboard.write([new ClipboardItem({ [item.blob.type]: item.blob })]);
      toast('Copied to clipboard');
      emit('ok', `Copied ${item.outName} to clipboard`);
    } else if (act === 'share') {
      const file = new File([item.blob], item.outName, { type: item.blob.type });
      if (!navigator.canShare?.({ files: [file] })) {
        throw new Error('Sharing this file is not supported here.');
      }
      await navigator.share({ files: [file], title: item.outName });
      emit('ok', `Shared ${item.outName}`);
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return; // user cancelled share
    const msg = err instanceof Error ? err.message : 'Action failed.';
    toast(msg, 'err');
    emit('err', `${act} failed: ${msg}`);
  }
}

async function downloadAll(): Promise<void> {
  const done = compressor.successful();
  if (done.length === 0) return;
  try {
    setStatus('Packaging ZIP…', 'busy');
    const { zipSync } = await import('fflate');
    const entries: Record<string, Uint8Array> = {};
    const used = new Set<string>();
    for (const item of done) {
      if (!item.blob || !item.outName) continue;
      let name = item.outName;
      let n = 2;
      while (used.has(name)) name = item.outName.replace(/(\.[^.]+)$/, `-${n++}$1`);
      used.add(name);
      entries[name] = new Uint8Array(await item.blob.arrayBuffer());
    }
    // Stored (level 0): the images are already compressed; re-zipping wastes CPU.
    const zipped = zipSync(entries, { level: 0 });
    downloadBlob(new Blob([zipped], { type: 'application/zip' }), 'pixpress-images.zip');
    emit('ok', `Packaged ${done.length} images into pixpress-images.zip`);
    toast('ZIP downloaded');
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Could not build the ZIP.';
    toast(msg, 'err');
    emit('err', `ZIP failed: ${msg}`);
  } finally {
    setStatus('Ready', 'idle');
  }
}

// ─────────────────────────────── ingestion ──────────────────────────────────

function ingest(files: FileList | File[] | null | undefined): void {
  if (!files) return;
  const list = Array.from(files);
  if (list.length === 0) return;
  batchActions.classList.remove('hidden');
  void compressor.add(list);
}

async function loadSample(): Promise<void> {
  try {
    emit('info', 'Loading sample image…');
    const res = await fetch('samples/demo.jpg');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const blob = await res.blob();
    // Route through the exact same path a real upload takes.
    ingest([new File([blob], 'sample.jpg', { type: blob.type || 'image/jpeg' })]);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    toast('Could not load the sample.', 'err');
    emit('err', `Sample failed: ${msg}`);
  }
}

dropzone.addEventListener('click', (e) => {
  if ((e.target as HTMLElement).closest('.glossary-link')) return;
  fileInput.click();
});
document.getElementById('sample-btn')?.addEventListener('click', (e) => {
  // The dropzone's own click opens the file picker — don't do both.
  e.stopPropagation();
  void loadSample();
});
dropzone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fileInput.click();
  }
});
fileInput.addEventListener('change', () => {
  ingest(fileInput.files);
  fileInput.value = '';
});

for (const evt of ['dragenter', 'dragover'] as const) {
  dropzone.addEventListener(evt, (e) => {
    e.preventDefault();
    dropzone.classList.add('dragging');
  });
}
for (const evt of ['dragleave', 'dragend'] as const) {
  dropzone.addEventListener(evt, (e) => {
    if (evt === 'dragleave' && dropzone.contains((e as DragEvent).relatedTarget as Node)) return;
    dropzone.classList.remove('dragging');
  });
}
dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropzone.classList.remove('dragging');
  ingest(e.dataTransfer?.files);
});

window.addEventListener('paste', (e) => {
  if (isModalOpen() || isCompareOpen()) return;
  const files: File[] = [];
  for (const it of Array.from(e.clipboardData?.items ?? [])) {
    if (it.kind === 'file') {
      const f = it.getAsFile();
      if (f) files.push(f);
    }
  }
  if (files.length) {
    e.preventDefault();
    ingest(files);
  }
});

// ─────────────────────────────── controls ───────────────────────────────────

function syncQualityVisibility(): void {
  qualityGroup.classList.toggle('disabled', !FORMATS[settings.format].lossy);
}

document.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const fmt = btn.dataset.format;
    if (!fmt || !isOutputFormat(fmt)) return;
    settings.format = fmt;
    localStorage.setItem(LS_FORMAT, fmt);
    document.querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => {
      b.setAttribute('aria-checked', String(b === btn));
    });
    syncQualityVisibility();
    emit('info', `Format set to ${FORMATS[fmt].label}`);
    compressor.setSettings({ ...settings });
  });
});

let qualityTimer: number | undefined;
qualityInput.addEventListener('input', () => {
  const q = Number(qualityInput.value);
  settings.quality = q;
  qualityVal.textContent = String(Math.round(q * 100));
  localStorage.setItem(LS_QUALITY, String(q));
  window.clearTimeout(qualityTimer);
  qualityTimer = window.setTimeout(() => compressor.setSettings({ ...settings }), 280);
});

resizeSelect.addEventListener('change', () => {
  const v = Number(resizeSelect.value);
  settings.maxDim = Number.isFinite(v) ? v : 0;
  localStorage.setItem(LS_MAXDIM, String(settings.maxDim));
  emit('info', settings.maxDim ? `Max size set to ${settings.maxDim}px` : 'Resize turned off');
  compressor.setSettings({ ...settings });
});

syncQualityVisibility();

document.getElementById('download-all')?.addEventListener('click', () => void downloadAll());
document.getElementById('clear-all')?.addEventListener('click', () => {
  for (const card of cards.values()) card.remove();
  cards.clear();
  compressor.reset();
  overall.classList.add('hidden');
  summary.classList.add('hidden');
  batchActions.classList.add('hidden');
  sbThroughput.textContent = '';
  setStatus('Ready', 'idle');
  emit('info', 'Cleared all images.');
});

// ──────────────────────────── modals & keys ─────────────────────────────────

document.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
  btn.addEventListener('click', () => openModal(btn.dataset.modal!));
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (isCompareOpen()) closeCompare();
  else if (isModalOpen()) closeModal();
});

window.addEventListener('beforeunload', () => compressor.destroy());
